import React, { useState, useEffect } from 'react';
import {
  Modal, ModalHeader, ModalBody, Button
} from "reactstrap";
import { Form } from "react-final-form";
import { Table } from 'antd';
import axios from 'axios';
import { flashError } from '@/shared/components/flash';
import { useDispatch } from 'react-redux';

const HierarchyThresholdModal = ({ organizationId, programId, onClose }) => {
  const dispatch = useDispatch();
  const [programs, setPrograms] = useState([]);
  const [checkedState, setCheckedState] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    fetchHierarchy();
  }, []);

  const flattenHierarchy = (nodes) => {
    let flat = [];
    nodes.forEach((node) => {
      flat.push({
        id: node.id,
        name: node.name,
        threshold: node.cascading_approval_min_threshold ?? '',
      });
      if (node.children_minimal?.length) {
        flat = flat.concat(flattenHierarchy(node.children_minimal));
      }
    });
    return flat;
  };

  const fetchHierarchy = async () => {
    setLoading(true);
    try {
        const res = await axios.get(
        `/organization/${organizationId}/program/${programId}/hierarchy-prepare`
        );
        if (res.status === 200) {
        // API returns mixed flat+nested — only use first item (root) and recurse its children_minimal
        const flat = flattenHierarchy([res.data[0]]);
        const sorted = flat.sort((a, b) => a.id - b.id);
        setPrograms(sorted);
        setCheckedState(sorted.map((p) => ({ id: p.id, checked: true })));
        }
    } catch (err) {
        flashError(dispatch, err?.response?.data);
    } finally {
        setLoading(false);
    }
  };

  const handleThresholdChange = (id, value) => {
    setPrograms((prev) =>
      prev.map((p) => (p.id === id ? { ...p, threshold: value } : p))
    );
  };

  const handleToggle = (index) => {
    if (programs[index].id === programId) return;
        const updated = [...checkedState];
        updated[index].checked = !updated[index].checked;
        setCheckedState(updated);
    };

    const handleSave = async () => {
    setSaving(true);
    const selectedIds = checkedState.filter((c) => c.checked).map((c) => c.id);
    const selectedPrograms = programs.filter((p) => selectedIds.includes(p.id));

    try {
        const res = await axios.put(
        `/organization/${organizationId}/program/${programId}/update-hierarchy`,
        {
            organization_id: organizationId,
            program_id: programId,
            programIds: selectedPrograms.map((p) => ({
            id: p.id,
            cascading_approval_min_threshold: p.threshold !== '' ? Number(p.threshold) : null,
            })),
        }
        );
        if (res.status === 200) {
        setResults(res.data);
        }
    } catch (err) {
        flashError(dispatch, err?.response?.data);
    } finally {
        setSaving(false);
    }
    };

  const editColumns = [
    {
      title: 'Program ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <a>{text}</a>,
    },
    {
      title: 'Program Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Threshold Amount',
      key: 'threshold',
      render: (_, record, index) => (
        <input
          type="number"
          className="form-control"
          style={{ width: '110px' }}
          value={record.threshold}
          disabled={!checkedState[index]?.checked}
          onChange={(e) => handleThresholdChange(record.id, e.target.value)}
        />
      ),
    },
    {
    title: 'Copy\\Update?',
    key: 'action',
    render: (_, record, index) => {
        const isCurrentProgram = record.id === programId;
    return (
        <div className="checkbox-custom2">
        <label className={`checkbox-btn${isCurrentProgram ? ' disabled' : ''}`} style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '6px' }}>
            <input
            className="checkbox-btn__checkbox"
            type="checkbox"
            name={`threshold-cb-${record.id}`}
            checked={checkedState[index]?.checked ?? true}
            disabled={record.id === programId}
            onChange={() => handleToggle(index)}
            value="true"
            />
            <span className="checkbox-btn__checkbox-custom">
            <svg className="mdi-icon" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
                <path d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" />
            </svg>
            </span>
            <span className="checkbox-btn__label">update</span>
        </label>
        </div>
    )},
    },
  ];

  const resultColumns = [
    {
      title: 'Program ID',
      dataIndex: 'id',
      key: 'id',
      render: (text) => <a>{text}</a>,
    },
    {
      title: 'Program Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Result',
      dataIndex: 'result',
      key: 'result',
    },
  ];

  return (
    <>
      {/* Edit view — matches existing isOpenHierarchyList modal */}
      <Modal
        className="modal-event-hierarchy-list modal-lg"
        isOpen={!results}
        toggle={onClose}
      >
        <ModalHeader>
          <div>
            Please select the programs you wish to update the threshold for, and set each amount individually.
          </div>
        </ModalHeader>
        <ModalBody className="modal-lg" style={{ textAlign: 'left' }}>
          <Form onSubmit={handleSave}>
            {({ handleSubmit }) => (
              <form className="form" onSubmit={handleSubmit}>
                <div style={{ padding: '10px', display: 'flex', gap: '10px' }}>
                  <Button
                    outline
                    className="btn btn-primary btn-sm"
                    color="#ffffff"
                    disabled={saving || loading}
                    onClick={handleSave}
                  >
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                  <Button
                    outline
                    color="primary"
                    className="btn btn-sm"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </div>
                {loading ? (
                  <p style={{ padding: '10px' }}>Loading hierarchy…</p>
                ) : (
                  <Table
                    pagination={false}
                    rowKey="id"
                    columns={editColumns}
                    dataSource={programs}
                  />
                )}
              </form>
            )}
          </Form>
        </ModalBody>
      </Modal>

      {/* Result view — matches existing isOpenHierarchyResult modal */}
      <Modal
        className="modal-event-hierarchy-list modal-lg"
        isOpen={!!results}
        toggle={onClose}
      >
        <ModalHeader>
          <div></div>
        </ModalHeader>
        <ModalBody className="modal-lg" style={{ textAlign: 'left' }}>
          <Table
            pagination={false}
            rowKey="id"
            columns={resultColumns}
            dataSource={results || []}
          />
          <div style={{ padding: '10px' }}>
            <Button
              outline
              color="primary"
              className="btn btn-sm"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export default HierarchyThresholdModal;